import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-190e0210"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "marketing-email-designer-content" }
const _hoisted_2 = { class: "panel" }
const _hoisted_3 = { class: "panel" }
const _hoisted_4 = { class: "panel" }
const _hoisted_5 = { class: "panel" }
const _hoisted_6 = { class: "panel preview-tab" }
const _hoisted_7 = { class: "preview-body-container" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { id: "preview-body" }, null, -1))

import { computed, inject, watch, ref } from "vue";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import SystemCodeSingleSelect from "../ui/system-codes/system-code-single-select.vue";
import { CodeType } from "acquaint-api/constants/CodeType";
import FieldValidationFreeTextbox from "../ui/inputs/field-validation-free-textbox.vue";
import { DxTabPanel, DxItem } from "devextreme-vue/tab-panel";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { EmailMarketingSettings } from "@/types/emails/EmailMarketingSettings";
import ColourPicker from "../ui/inputs/colour-picker.vue";
import ScrollView from "../ui/scroll-view.vue";
import { Body } from "@/types/generic/Body";
import { MarketingEmailType } from "@/enums/emails/MarketingEmailType";
import { MarketingEmailTabs } from "@/enums/emails/MarketingEmailTabs";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";


export default /*#__PURE__*/_defineComponent({
  __name: 'marketing-email-designer-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;

const onCancelClick = () => (showModal.value = false);

const marketingEmailDesignerData = ref<EmailMarketingSettings>();
const originalMarketingEmailDesignerData = ref<EmailMarketingSettings>();
const selectedTab = ref(MarketingEmailTabs.General);
const emailType = ref(MarketingEmailType.PropertyBulletin);
const previewContent = ref<Body>({ body: "" });
const loadingData = ref(false);
const displayCancelConfirmationModal = ref(false);

watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      return;
    }

    marketingEmailDesignerData.value = undefined;
    loadingData.value = true;
    displayCancelConfirmationModal.value = false;
    selectedTab.value = MarketingEmailTabs.General;

    if (!(await fetchMarketingEmailDesignerData())) {
      return;
    }

    if (originalMarketingEmailDesignerData.value) {
      marketingEmailDesignerData.value = JSON.parse(
        JSON.stringify(originalMarketingEmailDesignerData.value),
      );
    }

    // We are using the PropertyBulletin enum value as the default value. If a user opens the modal and type
    // value is already set to this enum value, we will call the fetchAndDisplayPreviewContent function.
    // Otherwise, we will set the type value to PropertyBulletin enum value, which will trigger the
    // fetchAndDisplayPreviewContent function to be called in the watch on the email type ref
    emailType.value = MarketingEmailType.PropertyBulletin;
    await fetchAndDisplayPreviewContent();

    loadingData.value = false;
  },
);

const fetchMarketingEmailDesignerData = async () => {
  try {
    originalMarketingEmailDesignerData.value = await apiClient.httpGet(
      "app/marketing/emaildesigner/settings",
    );
    return true;
  } catch (err) {
    marketingEmailDesignerData.value = undefined;
    originalMarketingEmailDesignerData.value = undefined;
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const fetchAndDisplayPreviewContent = async () => {
  await fetchPreviewEmailContent();

  var divToInsertHTMLInto = document.getElementById("preview-body");

  if (divToInsertHTMLInto) {
    divToInsertHTMLInto.innerHTML = previewContent.value.body;
  }
};

const fetchPreviewEmailContent = async () => {
  try {
    previewContent.value = await apiClient.httpPost(
      "app/marketing/emaildesigner/preview",
      { ...marketingEmailDesignerData.value, emailType: emailType.value },
    );
  } catch (err) {
    previewContent.value = { body: "" };
    displayErrorMessage(getErrorMessage(err));
  }
};

watch(
  () => emailType.value,
  () => {
    if (loadingData.value) {
      return;
    }

    fetchAndDisplayPreviewContent();
  },
);

watch(
  () => marketingEmailDesignerData.value,
  (value, oldValue) => {
    // When the modal is first opened, marketingEmailDesignerData is undefined and we don't want to call
    // fetchAndDisplayPreviewContent here then, only when a changes are made by the user.
    if (!oldValue || !value || loadingData.value) {
      return;
    }

    displayCancelConfirmationModal.value =
      JSON.stringify(marketingEmailDesignerData.value) !=
      JSON.stringify(originalMarketingEmailDesignerData.value);

    fetchAndDisplayPreviewContent();
  },
  { deep: true },
);

const updateMarketingEmailDesignerData = async () => {
  try {
    await apiClient.httpPutVoid(
      "app/marketing/emaildesigner/settings",
      marketingEmailDesignerData.value,
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const onSaveClick = async () => {
  if (await updateMarketingEmailDesignerData()) {
    displayCancelConfirmationModal.value = false;
    showModal.value = false;
  }
};

const emailTypeIdsToExclude: number[] = [
  MarketingEmailType.PropertyFeedbackRequest,
];

const tabPanelWidth = computed(() => {
  return screen.width <= 600 ? "100%" : "15rem";
});

const scrollDirection = computed(() => {
  return screen.width <= 600 ? "both" : "vertical";
});

const hideBlurInPreviewTab = computed(() => {
  return screen.width <= 600 ? true : false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalBase, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((showModal).value = $event)),
    title: "Marketing Email Designer",
    height: "700px",
    width: _unref(ModalWidth).Large,
    showCloseConfirmationPrompt: displayCancelConfirmationModal.value
  }, {
    content: _withCtx(() => [
      _createVNode(SystemCodeSingleSelect, {
        "code-type": _unref(CodeType).MarketingEmailType,
        "ids-to-exclude": emailTypeIdsToExclude,
        label: "Type",
        modelValue: emailType.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((emailType).value = $event)),
        readOnly: false
      }, null, 8, ["code-type", "modelValue"]),
      _createElementVNode("section", _hoisted_1, [
        (marketingEmailDesignerData.value)
          ? (_openBlock(), _createBlock(_unref(DxTabPanel), {
              key: 0,
              "scroll-by-content": true,
              "show-nav-buttons": true,
              "swipe-enabled": false,
              selectedIndex: selectedTab.value,
              "onUpdate:selectedIndex": _cache[19] || (_cache[19] = ($event: any) => ((selectedTab).value = $event)),
              width: tabPanelWidth.value
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DxItem), { title: "General" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_2, [
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.backgroundColour,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((marketingEmailDesignerData.value.backgroundColour) = $event)),
                        label: "Outer Background Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.bodyColour,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((marketingEmailDesignerData.value.bodyColour) = $event)),
                        label: "Body Background Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.bodyTextColour,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((marketingEmailDesignerData.value.bodyTextColour) = $event)),
                        label: "Body Text Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.footerDividerColour,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((marketingEmailDesignerData.value.footerDividerColour) = $event)),
                        label: "Footer Background Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.footerTextColour,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((marketingEmailDesignerData.value.footerTextColour) = $event)),
                        label: "Footer Text Colour"
                      }, null, 8, ["modelValue"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DxItem), { title: "Menu" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.menuColour,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((marketingEmailDesignerData.value.menuColour) = $event)),
                        label: "Background Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.menuItemColour,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((marketingEmailDesignerData.value.menuItemColour) = $event)),
                        label: "Button Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.menuItemTextColour,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((marketingEmailDesignerData.value.menuItemTextColour) = $event)),
                        label: "Button Text Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.menuItemSeperatorColour,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((marketingEmailDesignerData.value.menuItemSeperatorColour) = $event)),
                        label: "Button Seperator Colour"
                      }, null, 8, ["modelValue"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DxItem), { title: "Details" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_4, [
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.detailBorderColour,
                        "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((marketingEmailDesignerData.value.detailBorderColour) = $event)),
                        label: "Detail Border Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.detailBackgroundColour,
                        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((marketingEmailDesignerData.value.detailBackgroundColour) = $event)),
                        label: "Detail Background Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.headingColour,
                        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((marketingEmailDesignerData.value.headingColour) = $event)),
                        label: "Detail Heading Colour"
                      }, null, 8, ["modelValue"]),
                      _createVNode(ColourPicker, {
                        modelValue: marketingEmailDesignerData.value.detailTextColour,
                        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((marketingEmailDesignerData.value.detailTextColour) = $event)),
                        label: "Detail Text Colour"
                      }, null, 8, ["modelValue"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DxItem), { title: "Social Media" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      _createVNode(SystemCodeSingleSelect, {
                        "code-type": _unref(CodeType).MarketingEmailSocialMediaIconStyle,
                        label: "Icon Style",
                        modelValue: marketingEmailDesignerData.value.socialMediaIconStyle,
                        "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((marketingEmailDesignerData.value.socialMediaIconStyle) = $event)),
                        readOnly: false
                      }, null, 8, ["code-type", "modelValue"]),
                      _createVNode(FieldValidationFreeTextbox, {
                        label: "Facebook URL",
                        modelValue: marketingEmailDesignerData.value.facebookUrl,
                        "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((marketingEmailDesignerData.value.facebookUrl) = $event))
                      }, null, 8, ["modelValue"]),
                      _createVNode(FieldValidationFreeTextbox, {
                        label: "Twitter URL",
                        modelValue: marketingEmailDesignerData.value.twitterUrl,
                        "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((marketingEmailDesignerData.value.twitterUrl) = $event))
                      }, null, 8, ["modelValue"]),
                      _createVNode(FieldValidationFreeTextbox, {
                        label: "LinkedIn URL",
                        modelValue: marketingEmailDesignerData.value.linkedInUrl,
                        "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((marketingEmailDesignerData.value.linkedInUrl) = $event))
                      }, null, 8, ["modelValue"]),
                      _createVNode(FieldValidationFreeTextbox, {
                        label: "Instagram URL",
                        modelValue: marketingEmailDesignerData.value.instagramUrl,
                        "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((marketingEmailDesignerData.value.instagramUrl) = $event))
                      }, null, 8, ["modelValue"])
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["selectedIndex", "width"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(ScrollView, {
              scrollDirection: scrollDirection.value,
              hideBlur: hideBlurInPreviewTab.value
            }, {
              default: _withCtx(() => [
                _hoisted_8
              ]),
              _: 1
            }, 8, ["scrollDirection", "hideBlur"])
          ])
        ])
      ])
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Save',
          onClick: () => {
            onSaveClick();
          },
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          onClick: () => {
            onCancelClick();
          },
        }
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue", "width", "showCloseConfirmationPrompt"]))
}
}

})